import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router";

import { RiskMeter, asOfferState, asRiskLevel, useRobot } from "@/entities/robot";
import { useWallet } from "@/entities/wallet";
import { AmountPresets } from "@/features/amount-presets";
import { ROUTES } from "@/shared/config";
import { compareMoney, formatMoney, formatPercent } from "@/shared/lib";
import { AmountInput, Button } from "@/shared/ui";
import { ScreenSkeleton, ScreenState } from "@/widgets/screen-state";

/**
 * Шаг 1 покупки: сколько подключить (ТЗ §16).
 *
 * Figma: `Launch Amount` (22:157) на `AmountInput/Trading` (18:47) — свободный
 * ввод с пресетами. До перехода на минимальную сумму цена робота была
 * фиксированной, и блок стоял здесь только на чтение; теперь сумму выбирает
 * пользователь, и пресеты вернулись на своё место (DESIGN.md §8).
 *
 * Стартовое значение — рекомендованная сумма оператора, иначе минимум: экран
 * должен открываться с осмысленной цифрой, а не с пустым полем.
 *
 * Ни минимум, ни остаток здесь не проверяются «насмерть»: CTA гаснет только на
 * пустом поле, а окончательный отказ приходит от бэкенда и показывается у поля
 * на шаге подтверждения. Дублировать правило на клиенте значило бы завести
 * второй источник правды о том, что такое «слишком мало».
 *
 * У лимитированного робота (доработки §2, §10 «Partial capacity») база пресетов
 * — не баланс, а МЕНЬШЕЕ из баланса и личного остатка `limited_available`.
 * Остаток считает бэкенд: он один знает и пул, и уже внесённое этим человеком.
 * Здесь происходит только выбор одной из двух готовых строк — сравнение, а не
 * арифметика над деньгами, поэтому правило ТЗ №5 не нарушено.
 */
export function BuyAmountPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const robotId = Number(id);

  const robot = useRobot(robotId);
  const wallet = useWallet();
  const [amount, setAmount] = useState<string | null>(null);

  if (robot.isPending || wallet.isPending) return <ScreenSkeleton blocks={[170, 150, 158]} />;

  if (robot.isError || wallet.isError) {
    return (
      <ScreenState
        tone="loss"
        icon="warningFill"
        title={t("states.error.title")}
        body={t("states.error.body")}
        action={{
          label: t("states.error.action"),
          onClick: () => {
            void robot.refetch();
            void wallet.refetch();
          },
        }}
      />
    );
  }

  const minAmount = robot.data.min_amount;
  // `??` вместо `||`: рекомендованная сумма может быть не задана, но не может
  // быть пустой строкой — контракт отдаёт либо деньги, либо null.
  const value = amount ?? robot.data.recommended_amount ?? minAmount;
  const offer = robot.data.limited;
  const offerLeft = robot.data.limited_available;
  // Потолок ввода: остаток кампании, если он меньше баланса. Обе строки уже
  // посчитаны — берём одну из них, ничего не вычисляя.
  const ceiling =
    offerLeft !== null && offerLeft !== undefined &&
    compareMoney(offerLeft, wallet.data.balance) < 0
      ? offerLeft
      : wallet.data.balance;
  const enough = compareMoney(ceiling, minAmount) >= 0;
  // Кампания закрылась, пока пользователь шёл сюда: экран не притворяется, что
  // сумму можно ввести, — §10 «Pool full → CTA disabled».
  const closed = offer !== null && !offer.accepts_entries;

  return (
    <div className="flex flex-col gap-3.5 px-5 py-3">
      <header className="flex flex-col gap-1">
        <h1 className="text-h2 font-bold text-text-primary">
          {t("buy.title", { name: robot.data.name })}
        </h1>
        <span className="text-caption font-normal text-text-secondary">
          {t("buy.step", { current: 1 })}
        </span>
      </header>

      <section className="flex flex-col gap-2 rounded-[22px] border border-border-subtle bg-background-elevated p-[18px]">
        <div className="flex items-baseline justify-between">
          <span className="text-label font-medium text-text-secondary">{t("buy.amountTitle")}</span>
          <span className="text-caption font-normal text-text-secondary">
            {t("buy.available")} {formatMoney(wallet.data.balance, { suffix: "USDT" })}
          </span>
        </div>

        <AmountInput
          value={value}
          onChange={setAmount}
          hint={t("buy.minAmount", { amount: formatMoney(minAmount, { suffix: "USDT" }) })}
        />

        {/* Личный остаток — отдельной строкой под полем: он ограничивает ввод
            сильнее минимума, и узнавать о нём из отказа на следующем шаге
            пользователь не должен (§10 «Partial capacity»). */}
        {offerLeft !== null && offerLeft !== undefined && offer?.accepts_entries ? (
          <span className="text-caption font-medium text-text-secondary">
            {t("robots.limited.remaining", {
              amount: formatMoney(offerLeft, { suffix: "USDT" }),
            })}
          </span>
        ) : null}

        <AmountPresets balance={ceiling} min={minAmount} onPick={setAmount} />
      </section>

      <RiskMeter level={asRiskLevel(robot.data.risk_level)} maxDd={robot.data.declared_max_dd} />

      <section className="flex flex-col gap-1.5 rounded-[20px] border border-border-subtle bg-background-raised p-4">
        <span className="text-micro font-medium uppercase tracking-wide text-text-tertiary">
          {t("buy.estimateKicker")}
        </span>
        <span className="text-caption font-normal text-text-secondary">
          {t("buy.estimateRange", {
            from: formatPercent(robot.data.declared_max_dd),
            to: formatPercent(robot.data.performance.m12, { sign: "always" }),
          })}
        </span>
        <span className="text-micro font-normal text-text-tertiary">{t("buy.estimateNote")}</span>
      </section>

      <Button
        size="cta"
        block
        disabled={value === "" || !enough || closed}
        onClick={() => void navigate(ROUTES.robotBuyConfirm(robotId), { state: { amount: value } })}
      >
        {closed
          ? t(`robots.limited.state.${asOfferState(offer.state)}`)
          : enough
            ? t("buy.toConfirm")
            : t("buy.notEnough")}
      </Button>
    </div>
  );
}
